import React from "react";

interface PhaseData {
  phase_1: number;
  phase_2: number;
  phase_3: number;
  phase_4: number;
}

interface PerformanceData {
  practice_mcq: PhaseData;
  practice_coding: PhaseData;
  weekly_test: PhaseData;
  /** Present when project is selected; separate from practice_coding */
  project_coding?: PhaseData;
  final_test: PhaseData;
  total_students_count: number;
}

interface CandidatesPerformanceProps {
  performanceData: PerformanceData | null;
  loading: boolean;
  performanceFilter: string;
  width: string;
  /** When true (project selected), show Project Coding row and hide Weekly Test; when false (subject selected), show Weekly Test and hide Project Coding */
  isProjectSelected?: boolean;
}

const CandidatesPerformance: React.FC<CandidatesPerformanceProps> = ({
  performanceData,
  loading,
  performanceFilter,
  width = "60%",
  isProjectSelected = false,
}) => {
  const renderPerformanceBar = (
    label: string,
    phase1: number,
    phase2: number,
    phase3: number,
    phase4: number,
    totalStudents: number
  ) => {
    return (
      <div className="performance-bar">
        <span className="bar-label">{label}</span>
        <div className="bar-container">
          <div
            className="bar-segment orange"
            style={{
              width: `${(phase1 / totalStudents) * 100}%`,
            }}
            title={`${phase1} students (${((phase1 / totalStudents) * 100).toFixed(1)}%)`}
          ></div>
          <div
            className="bar-segment teal"
            style={{
              width: `${(phase2 / totalStudents) * 100}%`,
            }}
            title={`${phase2} students (${((phase2 / totalStudents) * 100).toFixed(1)}%)`}
          ></div>
          <div
            className="bar-segment yellow"
            style={{
              width: `${(phase3 / totalStudents) * 100}%`,
            }}
            title={`${phase3} students (${((phase3 / totalStudents) * 100).toFixed(1)}%)`}
          ></div>
          <div
            className="bar-segment dark-green"
            style={{
              width: `${(phase4 / totalStudents) * 100}%`,
            }}
            title={`${phase4} students (${((phase4 / totalStudents) * 100).toFixed(1)}%)`}
          ></div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="performance-container flex-grow-1 w-100 d-flex flex-column min-h-0"
      style={{ width: width, maxWidth: width, boxSizing: "border-box" }}
    >
      <div className="section-header">
        <h3>Candidates Performance</h3>
        <div className="d-flex align-items-center gap-2">
          {performanceData && (
            <span className="small text-muted fw-medium">
              Total Students: {performanceData.total_students_count}
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center p-5">
          <div>Loading performance data...</div>
        </div>
      ) : performanceData ? (
        <>
          <div className="performance-bars flex-grow-1">
            {renderPerformanceBar(
              "Practice MCQ",
              performanceData.practice_mcq.phase_1,
              performanceData.practice_mcq.phase_2,
              performanceData.practice_mcq.phase_3,
              performanceData.practice_mcq.phase_4,
              performanceData.total_students_count
            )}
            {renderPerformanceBar(
              "Practice Coding",
              performanceData.practice_coding.phase_1,
              performanceData.practice_coding.phase_2,
              performanceData.practice_coding.phase_3,
              performanceData.practice_coding.phase_4,
              performanceData.total_students_count
            )}
            {isProjectSelected ? (
              performanceData.project_coding ? (
                renderPerformanceBar(
                  "Project Coding",
                  performanceData.project_coding.phase_1,
                  performanceData.project_coding.phase_2,
                  performanceData.project_coding.phase_3,
                  performanceData.project_coding.phase_4,
                  performanceData.total_students_count
                )
              ) : null
            ) : (
              renderPerformanceBar(
                "Weekly Test",
                performanceData.weekly_test.phase_1,
                performanceData.weekly_test.phase_2,
                performanceData.weekly_test.phase_3,
                performanceData.weekly_test.phase_4,
                performanceData.total_students_count
              )
            )}
            {renderPerformanceBar(
              "Final Test",
              performanceData.final_test.phase_1,
              performanceData.final_test.phase_2,
              performanceData.final_test.phase_3,
              performanceData.final_test.phase_4,
              performanceData.total_students_count
            )}
          </div>
          <div className="performance-legend">
            <div className="legend-item">
              <div className="legend-color orange"></div>
              <span className="legend-text">Beginner</span>
              <span className="legend-percentage">0-25%</span>
            </div>
            <div className="legend-item">
              <div className="legend-color teal"></div>
              <span className="legend-text">Intermediate</span>
              <span className="legend-percentage">26-50%</span>
            </div>
            <div className="legend-item">
              <div className="legend-color yellow"></div>
              <span className="legend-text">Advanced</span>
              <span className="legend-percentage">51-75%</span>
            </div>
            <div className="legend-item">
              <div className="legend-color dark-green"></div>
              <span className="legend-text">Expert</span>
              <span className="legend-percentage">76-100%</span>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center p-5 text-muted">
          {performanceFilter === "all"
            ? "No performance data available"
            : "No performance data available for this filter"}
        </div>
      )}
    </div>
  );
};

export default CandidatesPerformance;
